import React, { useState, useEffect, useRef } from "react";
import {
	Card,
	Input,
	Button,
	Typography,
	Space,
	Avatar,
	Spin,
	Tag,
	Tooltip,
	Collapse,
} from "antd";
import {
	SendOutlined,
	RobotOutlined,
	UserOutlined,
	ClockCircleOutlined,
	HistoryOutlined,
	SettingOutlined,
} from "@ant-design/icons";
import axios from "axios";
import "./AIAssistant.css";
import QuickActions from "./QuickActions";
import ConversationHistory from "./ConversationHistory";
import UserPreferences from "./UserPreferences";
import Layout from "../layout/Layout";

const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

/**
 * AI Executive Assistant Chat Interface
 * Main component for conversational AI interaction
 */
const AIAssistant = ({ userId = localStorage.getItem("userId") }) => {
	const [messages, setMessages] = useState([]);
	const [input, setInput] = useState("");
	const [loading, setLoading] = useState(false);
	const [sessionId, setSessionId] = useState(null);
	const [showHistory, setShowHistory] = useState(false);
	const [showPreferences, setShowPreferences] = useState(false);
	const messagesEndRef = useRef(null);

	// Auto-scroll to bottom when new messages arrive
	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	useEffect(() => {
		scrollToBottom();
	}, [messages]);

	// Initialize with welcome message
	useEffect(() => {
		setMessages([
			{
				role: "assistant",
				content:
					"Hello! I'm your executive assistant for 57Concrete. I can help you analyze sales performance, customer insights, and operational metrics. What would you like to know?",
				timestamp: new Date(),
				agentType: "supervisor",
			},
		]);
	}, []);

	// Send message to API
	const sendMessage = async (messageText = input) => {
		if (!messageText.trim()) return;

		const userMessage = {
			role: "user",
			content: messageText,
			timestamp: new Date(),
		};

		// Add user message to UI immediately
		setMessages((prev) => [...prev, userMessage]);
		setInput("");
		setLoading(true);

		try {
			const response = await axios.post("/api/agent/chat", {
				message: messageText,
				userId,
				sessionId,
			});

			if (response.data.success) {
				// Update session ID if first message
				if (!sessionId) {
					setSessionId(response.data.sessionId);
				}

				// Add assistant response
				const assistantMessage = {
					role: "assistant",
					content: response.data.response,
					timestamp: new Date(),
					agentType: response.data.agentType,
					metrics: response.data.metrics,
					reflection: response.data.reflection,
				};

				setMessages((prev) => [...prev, assistantMessage]);
			} else {
				const errorMessage = {
					role: "assistant",
					content:
						response.data.error ||
						"Sorry, I encountered an error processing your request. Please try again.",
					timestamp: new Date(),
					isError: true,
				};
				setMessages((prev) => [...prev, errorMessage]);
			}
		} catch (error) {
			console.error("Error sending message:", error);
			const errorMessage = {
				role: "assistant",
				content:
					"Sorry, I encountered an error processing your request. Please try again.",
				timestamp: new Date(),
				isError: true,
			};
			setMessages((prev) => [...prev, errorMessage]);
		} finally {
			setLoading(false);
		}
	};

	// Handle enter key
	const handleKeyPress = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			sendMessage();
		}
	};

	// Handle quick action click
	const handleQuickAction = (query) => {
		sendMessage(query);
	};

	// Format timestamp
	const formatTime = (date) => {
		return new Date(date).toLocaleTimeString("en-US", {
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	// Get agent type color
	const getAgentColor = (agentType) => {
		const colors = {
			sales_analytics: "blue",
			customer_insights: "green",
			supervisor: "purple",
			reflection: "orange",
		};
		return colors[agentType] || "default";
	};

	// Get agent type label
	const getAgentLabel = (agentType) => {
		const labels = {
			sales_analytics: "Sales Analytics",
			customer_insights: "Customer Insights",
			supervisor: "Assistant",
			reflection: "Quality Check",
		};
		return labels[agentType] || agentType;
	};

	return (
		<Layout title="AI Chat">
			<div className="ai-assistant-container">
				<Card
					className="ai-assistant-card"
					title={
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<Space>
								<RobotOutlined style={{ fontSize: "24px", color: "#1890ff" }} />
								<Title level={4} style={{ margin: 0 }}>
									Jap Executive Assistant
								</Title>
							</Space>
							<Space>
								<Tooltip title="Conversation History">
									<Button
										icon={<HistoryOutlined />}
										onClick={() => setShowHistory(!showHistory)}
										type={showHistory ? "primary" : "default"}
									/>
								</Tooltip>
								<Tooltip title="Preferences">
									<Button
										icon={<SettingOutlined />}
										onClick={() => setShowPreferences(!showPreferences)}
										type={showPreferences ? "primary" : "default"}
									/>
								</Tooltip>
							</Space>
						</div>
					}
				>
					{/* Preferences Panel */}
					{showPreferences && (
						<UserPreferences
							userId={userId}
							onClose={() => setShowPreferences(false)}
						/>
					)}

					{/* History Panel */}
					{showHistory && (
						<ConversationHistory
							userId={userId}
							sessionId={sessionId}
							onClose={() => setShowHistory(false)}
							onSelectMessage={handleQuickAction}
						/>
					)}

					{/* Quick Actions - only show when no messages yet (except welcome message) */}
					{!showHistory && !showPreferences && messages.length <= 1 && (
						<QuickActions onActionClick={handleQuickAction} />
					)}

					{/* Messages Area */}
					<div className="messages-container">
						{messages.map((message, index) => (
							<div
								key={index}
								className={`message ${
									message.role === "user" ? "user-message" : "assistant-message"
								}`}
							>
								<Space align="start" style={{ width: "100%" }}>
									<Avatar
										icon={
											message.role === "user" ? (
												<UserOutlined />
											) : (
												<RobotOutlined />
											)
										}
										style={{
											backgroundColor:
												message.role === "user" ? "#87d068" : "#1890ff",
										}}
									/>
									<div className="message-content" style={{ flex: 1 }}>
										<div className="message-header">
											<Text strong>
												{message.role === "user" ? "You" : "Assistant"}
											</Text>
											<Space size="small">
												{message.agentType && (
													<Tag
														color={getAgentColor(message.agentType)}
														style={{ fontSize: "10px" }}
													>
														{getAgentLabel(message.agentType)}
													</Tag>
												)}
												<Text type="secondary" style={{ fontSize: "12px" }}>
													<ClockCircleOutlined />{" "}
													{formatTime(message.timestamp)}
												</Text>
											</Space>
										</div>
										<Paragraph
											className="message-text"
											style={{
												whiteSpace: "pre-wrap",
												marginTop: "8px",
												color: message.isError ? "#ff4d4f" : "inherit",
											}}
										>
											{message.content}
										</Paragraph>

										{/* Show follow-up questions if available */}
										{message.reflection?.followUpQuestions &&
											message.reflection.followUpQuestions.length > 0 && (
												<Collapse
													ghost
													size="small"
													style={{ marginTop: "12px" }}
												>
													<Panel
														header={
															<Text type="secondary">
																💡 Follow-up questions
															</Text>
														}
														key="1"
													>
														<Space
															direction="vertical"
															size="small"
															style={{ width: "100%" }}
														>
															{message.reflection.followUpQuestions.map(
																(question, idx) => (
																	<Button
																		key={idx}
																		type="link"
																		size="small"
																		onClick={() => handleQuickAction(question)}
																		style={{
																			textAlign: "left",
																			height: "auto",
																			padding: "4px 0",
																		}}
																	>
																		{question}
																	</Button>
																)
															)}
														</Space>
													</Panel>
												</Collapse>
											)}

										{/* Show metrics for debugging/transparency */}
										{message.metrics && (
											<div style={{ marginTop: "8px" }}>
												<Text type="secondary" style={{ fontSize: "11px" }}>
													Response time: {message.metrics.responseTime}ms
													{message.metrics.toolsUsed > 0 &&
														` • Tools used: ${message.metrics.toolsUsed}`}
												</Text>
											</div>
										)}
									</div>
								</Space>
							</div>
						))}

						{/* Loading indicator */}
						{loading && (
							<div className="message assistant-message">
								<Space align="start">
									<Avatar
										icon={<RobotOutlined />}
										style={{ backgroundColor: "#1890ff" }}
									/>
									<div className="message-content">
										<Spin size="small" />
										<Text type="secondary" style={{ marginLeft: "12px" }}>
											Thinking...
										</Text>
									</div>
								</Space>
							</div>
						)}

						<div ref={messagesEndRef} />
					</div>

					{/* Input Area */}
					<div className="input-container">
						<Space.Compact style={{ width: "100%" }}>
							<TextArea
								value={input}
								onChange={(e) => setInput(e.target.value)}
								onKeyPress={handleKeyPress}
								placeholder="Ask me about sales, customers, or analytics..."
								autoSize={{ minRows: 1, maxRows: 4 }}
								disabled={loading}
								style={{ resize: "none" }}
							/>
							<Button
								type="primary"
								icon={<SendOutlined />}
								onClick={() => sendMessage()}
								loading={loading}
								disabled={!input.trim()}
							>
								Send
							</Button>
						</Space.Compact>
						<Text
							type="secondary"
							style={{ fontSize: "11px", marginTop: "4px", display: "block" }}
						>
							Press Enter to send, Shift+Enter for new line
						</Text>
					</div>
				</Card>
			</div>
		</Layout>
	);
};

export default AIAssistant;
